import React, { useState, useCallback } from 'react';
import Card from 'components/cards/Card01';
import Datepicker from 'components/actions/DatePicker';
import AnalyticsService from 'api/services/AnalyticsService';
import { useTranslation } from 'react-i18next';
import { getDateRangeString, getI18nValue, getChannelLanguageString } from 'utils/Utils';
import { useAxios } from 'hooks/useAxios';
import LayoutWrapper from 'components/LayoutWrapper';
import { MdCheckCircle, MdTimer, MdTrendingUp, MdCancel, MdContactPage, MdOutlineStackedBarChart, MdOutlineError, MdSupportAgent } from 'react-icons/md';
import ChannelSelect from './ChannelSelect';
import LanguageSelect from './LanguageSelect';
import { ExportCSV } from 'components/ExportCSV';

function ConversationQuality() {
    const { t } = useTranslation();

    const [selectedDateRanges, setSelectedDateRanges] = useState({
        startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
        endDate: new Date(),
        key: 'selection',
    });

    const [channel, setChannel] = useState('');
    const [language, setLanguage] = useState('');

    const handleDateChange = useCallback((newRanges) => setSelectedDateRanges(newRanges), []);

    const [data, error, loading] = useAxios(
        AnalyticsService.getConversationQuality(selectedDateRanges, channel, language)
    );

    return (
        <div className='p-4 col-span-full sm:col-span-full xl:col-span-full bg-white shadow-sm rounded-sm border border-gray-200 min-h-[320px]'>
            <div className="sm:flex sm:justify-between sm:items-center border-b border-gray-100 mb-4">
                <div className='mb-4'>
                    <header className='text-xl font-semibold text-gray-800'>{t(getI18nValue('analytics.conversationQuality', 'title'))}</header>
                    <p className='text-sm font-light'>{t(getI18nValue('analytics.conversationQuality', 'description'))}</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap mb-6 p-4 bg-white shadow-sm rounded-sm border border-gray-200">
                    <ExportCSV csvData={data} disabled={!data} fileName={`conversation_quality_${getDateRangeString(selectedDateRanges) + getChannelLanguageString(channel, language)}`} />
                    <ChannelSelect value={channel} onChange={setChannel} />
                    <LanguageSelect value={language} onChange={setLanguage} />
                    <Datepicker callBack={handleDateChange} />
                </div>
            </div>
            <LayoutWrapper error={error} loading={loading} variant="cards" isEmpty={!loading && !data}>
                <div className='grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2'>
                    {!!data && Object.entries(data).map((item, i) => (
                        <Card
                            title={t(getI18nValue(`analytics.conversationQuality.${item[0]}`, 'title'))}
                            description={t(getI18nValue(`analytics.conversationQuality.${item[0]}`, 'description'))}
                            value={item[1]}
                            icon={getIcon(item[0])}
                            key={'cq_' + item[0]}
                        />
                    ))}
                </div>
            </LayoutWrapper>
        </div>
    );
}

export default ConversationQuality;

const getIcon = (key) => {
    const iconList = {
        total_flows:         <MdOutlineStackedBarChart />,
        resolved_flows:      <MdCheckCircle />,
        unresolved_flows:    <MdOutlineError />,
        abandoned_flows:     <MdCancel />,
        escalated_flows:     <MdSupportAgent />,
        resolution_rate:     <MdTrendingUp />,
        avg_resolution_time: <MdTimer />,
        fcr_rate:            <MdContactPage />,
        default:             <div className='w-5 h-5' />,
    };
    return iconList[key] || iconList['default'];
};
